import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOComponent from "../components/SEOComponent";
import DonationWaysTabs from "../components/DonationWaysTabs";
import Reveal from "../components/animations/Reveal";
import CountUp from "../components/animations/CountUp";
import { useGsap } from "../animations/hooks";
import {
    SplitBRString,
    getRandomStaffData,
    replaceStringWithShandeAge,
    replaceStringWithShandeChildAmount,
} from "../helpers/dataHelper";
import { getLanguage } from "../helpers/storageHelper";
import { monthlyExpenses, monthlyExpenseTotal } from "../helpers/StaticData/donationDetailsData";
import "./DonationDetails.css";

function DonationDetails() {
    const { t } = useTranslation();
    const address = process.env.REACT_APP_SHANDE_ADDRESS;
    const language = getLanguage();

    const description = replaceStringWithShandeChildAmount(replaceStringWithShandeAge(t("donationDetailsDescription")));
    const walkinDescription = replaceStringWithShandeAge(t("walkinDonationDescription"));
    const expensesDescription = replaceStringWithShandeChildAmount(t("expensesDescription"));

    const suppliesWhatsAppUrl = React.useMemo(() => {
        const contact = getRandomStaffData("Contact", "Marketing");
        const message = String(language) === "1" ? "你好，我想捐赠物资" : "Hello, I would like to donate supplies";
        return `https://api.whatsapp.com/send?phone=6${contact}&text=${encodeURIComponent(message)}`;
    }, [language]);
    const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address ?? "")}`;

    const pills = [
        { value: replaceStringWithShandeAge("[shandeAge]"), label: t("statYearsLabel") },
        { value: replaceStringWithShandeChildAmount("[shandeCurrentChildAmount]"), label: t("statCurrentLabel") },
        { value: `RM ${monthlyExpenseTotal.toLocaleString()}`, label: t("monthlyExpenses") },
    ];

    // Hero entrance: copy rises in sequence while the photo settles into place.
    const heroRef = useGsap((gsap, el) => {
        gsap.timeline({ defaults: { ease: "power3.out" } })
            .from(el.querySelector(".landing-page-kicker"), { y: 18, autoAlpha: 0, duration: 0.5 })
            .from(el.querySelector(".landing-page-title"), { y: 36, autoAlpha: 0, duration: 0.8 }, "-=0.25")
            .from(el.querySelectorAll(".landing-page-summary li"), { y: 20, autoAlpha: 0, duration: 0.6, stagger: 0.08 }, "-=0.45")
            .from(el.querySelectorAll(".landing-page-pill"), { y: 20, autoAlpha: 0, duration: 0.5, stagger: 0.1 }, "-=0.3")
            .from(el.querySelector(".landing-page-hero-frame"), { x: 40, scale: 0.95, autoAlpha: 0, duration: 1 }, 0.3)
            .from(el.querySelector(".landing-page-hero-note"), { y: 20, autoAlpha: 0, duration: 0.6 }, "-=0.4");
    });

    // Expense bars grow to their share of the monthly total as they scroll in.
    const expensesRef = useGsap((gsap, el) => {
        gsap.from(el.querySelectorAll(".donation-expense-bar-fill"), {
            scaleX: 0,
            transformOrigin: "left center",
            duration: 1.2,
            ease: "power3.out",
            stagger: 0.1,
            scrollTrigger: { trigger: el, once: true },
        });
    });

    const donateStructuredData = {
        "@context": "https://schema.org",
        "@type": "DonateAction",
        "name": "Donation details for Shan De Children's Welfare Home",
        "url": "https://www.shandejohor.org.my/donationDetails",
        "recipient": { "@type": "NGO", "name": "Pertubuhan Kebajikan Shan De" },
    };

    return (
        <>
        <SEOComponent
            title="Donation Details"
            description="See how Shan De Children's Welfare Home spends every ringgit, and the ways you can donate: bank transfer, DuitNow QR, supplies, or a walk-in visit to the home in Johor Bahru."
            keywords="donation details, monthly expenses, bank transfer, duitnow, donate supplies, walk-in donation"
            url="/donationDetails"
            structuredData={donateStructuredData}
        />

        {/* ---------- Hero ---------- */}
        <section ref={heroRef} className="landing-page-hero donation-hero">
            <div className="container">
                <div className="landing-page-hero-grid">
                    <div className="landing-page-hero-copy">
                        <div className="landing-page-kicker">{t("donationEyebrow")}</div>
                        <h1 className="landing-page-title">{t("donationDetails")}</h1>
                        <div className="landing-page-summary">{SplitBRString(description)}</div>
                        <div className="landing-page-meta">
                            {pills.map((pill) => (
                                <div className="landing-page-pill" key={pill.label}>
                                    <div className="landing-page-pill-value">{pill.value}</div>
                                    <div className="landing-page-pill-label">{pill.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="landing-page-hero-media">
                        <div className="landing-page-hero-frame">
                            <img className="landing-page-hero-image" src="assets/img/donation_details/donation_title_img.png" alt={t("donationDetails")} />
                        </div>
                        <div className="landing-page-hero-note">
                            <strong>{t("donationHeroNoteTitle")}</strong>
                            <span>{t("donationHeroNote")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* ---------- Monthly expenses ---------- */}
        <section className="landing-section landing-section-alt">
            <div className="container">
                <Reveal className="landing-page-section-title">
                    <div className="landing-eyebrow">{t("expensesEyebrow")}</div>
                    <h2 className="landing-heading">{t("expensesTitle")}</h2>
                    <p className="landing-section-desc">{expensesDescription}</p>
                </Reveal>
                <div ref={expensesRef} className="donation-expenses-grid">
                    <Reveal className="donation-expense-list" stagger={0.08}>
                        {monthlyExpenses.map((item) => {
                            const pct = item.amount ? Math.round((item.amount / monthlyExpenseTotal) * 100) : null;
                            return (
                                <div className="donation-expense" data-reveal key={item.key}>
                                    <div className="donation-expense-icon">
                                        <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                                    </div>
                                    <div className="donation-expense-body">
                                        <div className="donation-expense-top">
                                            <div>
                                                <span className="donation-expense-name">{t(item.labelKey)}</span>
                                                <span className={"donation-expense-tag" + (item.fixed ? "" : " is-variable")}>
                                                    {item.fixed ? t("expenseFixed") : t("expenseVariable")}
                                                </span>
                                            </div>
                                            <span className="donation-expense-amount">
                                                {item.amount ? `RM ${item.amount.toLocaleString()}` : t("expenseVaries")}
                                            </span>
                                        </div>
                                        <div className="donation-expense-bar">
                                            <div
                                                className={"donation-expense-bar-fill" + (pct === null ? " is-unknown" : "")}
                                                style={{ width: pct === null ? "100%" : `${Math.max(pct, 2)}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </Reveal>
                    <Reveal className="donation-expense-total" delay={0.2}>
                        <div className="donation-expense-total-label">{t("expensesTotalLabel")}</div>
                        <div className="donation-expense-total-value">
                            <CountUp value={monthlyExpenseTotal} prefix="RM " duration={2.2} />
                        </div>
                        <p className="donation-expense-total-note">{t("expensesTotalNote")}</p>
                        <Link className="landing-btn landing-btn-primary" to="/donate">{t("donateUs")}</Link>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* ---------- Donation ways ---------- */}
        <section className="landing-section">
            <div className="container">
                <Reveal className="landing-page-section-title center">
                    <div className="landing-eyebrow">{t("donationEyebrow")}</div>
                    <h2 className="landing-heading">{t("donationWays")}</h2>
                    <p className="landing-section-desc">{t("donationWaysDescription")}</p>
                </Reveal>
                <Reveal y={40}>
                    <DonationWaysTabs />
                </Reveal>
            </div>
        </section>

        {/* ---------- Walk-in donation ---------- */}
        <section className="landing-section landing-section-alt">
            <div className="container">
                <Reveal className="donation-walkin-grid" stagger={0.15}>
                    <div className="donation-walkin-media" data-reveal>
                        <img src="assets/img/donation_details/shande_home.png" alt={t("walkinDonation")} />
                    </div>
                    <div className="donation-walkin-card" data-reveal>
                        <div className="landing-eyebrow">{t("walkinEyebrow")}</div>
                        <h2 className="landing-heading">{t("walkinDonation")}</h2>
                        <div className="donation-walkin-desc">{SplitBRString(walkinDescription)}</div>
                        <ul className="landing-contact-list">
                            <li>
                                <span className="landing-contact-icon"><i className="fa-solid fa-location-dot" aria-hidden="true" /></span>
                                <div>
                                    <div className="landing-contact-label">{t("address")}</div>
                                    <div className="landing-contact-value">{address}</div>
                                </div>
                            </li>
                            <li>
                                <span className="landing-contact-icon"><i className="fa-regular fa-clock" aria-hidden="true" /></span>
                                <div>
                                    <div className="landing-contact-label">{t("openingHours")}</div>
                                    <div className="landing-contact-value">{t("openingHoursValue")}</div>
                                </div>
                            </li>
                        </ul>
                        <div className="donation-walkin-actions">
                            <a className="landing-btn landing-btn-primary" href={directionsUrl} target="_blank" rel="noreferrer">{t("getDirections")}</a>
                            <a className="landing-btn landing-btn-ghost" href={suppliesWhatsAppUrl} target="_blank" rel="noreferrer">{t("whatsappUs")}</a>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>

        {/* ---------- CTA ---------- */}
        <section className="landing-cta">
            <Reveal className="container" stagger={0.1}>
                <h2 className="landing-cta-title" data-reveal>{t("donationCtaTitle")}</h2>
                <p className="landing-cta-desc" data-reveal>{t("donationCtaDesc")}</p>
                <div className="landing-cta-actions" data-reveal>
                    <Link className="landing-btn landing-btn-dark" to="/donate">{t("donateUs")}</Link>
                    <a className="landing-btn landing-btn-ghost" href={suppliesWhatsAppUrl} target="_blank" rel="noreferrer">{t("donateSupplies")}</a>
                </div>
                <div className="landing-cta-meta" data-reveal>{t("ctaMeta")}</div>
            </Reveal>
        </section>
        </>
    );
}
export default DonationDetails;
